/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { LogDetails } from "../../types";

/**
 * Shared omit-empty wire helpers for building analytics `LogDetails` payloads.
 * Reused across analytics domains (playback session, search screen, …).
 */

/**
 * Sets `key` to `value` only when non-empty (omit-empty wire policy).
 */
export const putString = (
  target: LogDetails,
  key: string,
  value: string | undefined
): void => {
  if (value !== undefined && value.trim() !== "") {
    target[key] = value;
  }
};

/**
 * Sets `key` to the formatted `value` only when finite and non-zero
 * (omit-empty wire policy — matches Android's `takeIf { it != 0 }`).
 */
export const putNumber = (
  target: LogDetails,
  key: string,
  value: number | undefined,
  format: (value: number) => string = (value) => String(value)
): void => {
  if (value !== undefined && Number.isFinite(value) && value !== 0) {
    target[key] = format(value);
  }
};

/** Epoch-seconds timestamp formatted to 3 decimals, e.g. `1774456113.650`. */
export const formatEpochSeconds3dp = (value: number): string =>
  value.toFixed(3);

/** Bitrate (Mbps) formatted to 2 decimals. */
export const formatMbps2dp = (value: number): string => value.toFixed(2);

/**
 * Sets `key` to `"true"`/`"false"` when defined. Unlike `putString`/`putNumber`,
 * `false` is a meaningful value and is not omitted.
 */
export const putBoolean = (
  target: LogDetails,
  key: string,
  value: boolean | undefined
): void => {
  if (value !== undefined) {
    target[key] = value ? "true" : "false";
  }
};

/** Percentage formatted to 1 decimal. */
export const formatPercent1dp = (value: number): string => value.toFixed(1);

/**
 * Rounds to the nearest whole number — avoids floating-point noise (e.g. from
 * `performance.now()`/player-stats diffs) leaking into ms/byte/bps fields.
 */
export const formatRoundedInteger = (value: number): string =>
  String(Math.round(value));
